import * as React from 'react'
import { Appbar, IconButton } from 'react-native-paper'
import { StackHeaderProps, StackNavigationProp } from '@react-navigation/stack'
import {
  AppNavigation,
  AppNavigatorParams,
} from '../../../../../navigation/types'
import { View } from 'react-native'
import { Text } from 'react-native-paper'
import { useTheme } from '../../../../../hooks/theme.hook'
import { IconClose, IconCluster } from '../../../../../utils/meldd-icons'
import { useHeaderButton } from '../../../../../contexts/header-button.provider'
import { RemainingTimeIndicator } from '../components/remaining-time.indicator'
import { Article, Contribution } from '../types'
import { Env } from '../../../../../env'
import { useTranslation } from 'react-i18next'
import { createStyle } from '../../../../../contexts/global-styles.provider'
import PerspectiveScreenBarMenu from './perspective-menu.header'
import Row from '../../../../../@ui/containers/grid/row'
import { useNavigation } from '@react-navigation/native'
import { ContributionCount } from '../components/contribution.count'
import { EyeToggleSwitch } from '../../../../../@ui/form/eye-toggle.switch'
import { useIsMobileDevice } from '../../../../../hooks/is-mobile.hook'
import {
  ContributionStatus,
  ContributionType,
  useClusterByIdQuery,
} from '../../../../../generated/graphql'

export interface PerspectiveNavigationBarProps extends StackHeaderProps {
  navigation: StackNavigationProp<AppNavigatorParams>
}

export const EditingPerspectiveNavigationBar = ({
  reservation,
}: {
  reservation: Contribution
}) => {
  const { handler: cancelHandler, disabled: cancelDisabled } =
    useHeaderButton('cancelContribution')
  const theme = useTheme()
  const styles = useStyle()

  return (
    <Appbar.Header
      style={[
        styles.container,
        { backgroundColor: theme.palettes.primary[96]! },
      ]}
    >
      {cancelHandler && (
        <IconButton
          disabled={cancelDisabled}
          mode={'outlined'}
          icon={IconClose}
          style={{ margin: 0 }}
          onPress={cancelHandler}
        />
      )}
      <RemainingTimeIndicator
        maxDuration={Env.MAX_RESERVATION_DURATION}
        validUntil={new Date(reservation.reservedUntil).getTime()}
      />
    </Appbar.Header>
  )
}

const ArticleModeButton = () => {
  const { t } = useTranslation('menuItems')
  const isMobile = useIsMobileDevice()
  const { meta: detailsVisible, setMeta: setMode } = useHeaderButton<
    'history-mode' | 'read-mode'
  >('perspectiveMode')

  const isHistoryMode = detailsVisible === 'history-mode'

  return (
    <View
      style={{
        marginRight: isMobile ? 0 : 10,
        transform: isMobile ? [{ scale: 0.8 }] : undefined,
      }}
    >
      <EyeToggleSwitch
        value={isHistoryMode}
        onValueChange={(value) => {
          setMode(value ? 'history-mode' : 'read-mode')
        }}
      />
    </View>
  )
}

const BackButton = () => {
  const { handler: backButton } = useHeaderButton('navigation')
  const navigation = useNavigation<AppNavigation>()

  const goBack = React.useCallback(() => {
    return backButton ? backButton() : navigation.goBack()
  }, [backButton, navigation])

  return (
    <Appbar.BackAction onPress={() => goBack()} style={{ marginLeft: -10 }} />
  )
}

export function PerspectiveNavigationBar({
  navigation,
}: PerspectiveNavigationBarProps) {
  const styles = useStyle()
  const { meta: reservation } =
    useHeaderButton<Contribution>('saveContribution')
  const { meta: perspective } = useHeaderButton<Article>('perspective')
  const { meta: mode } = useHeaderButton<'history-mode' | 'read-mode'>(
    'perspectiveMode'
  )

  const isEditing = !!reservation

  if (isEditing) {
    return <EditingPerspectiveNavigationBar reservation={reservation} />
  }

  const isHistoryMode = mode === 'history-mode'
  return (
    <Appbar.Header style={styles.container}>
      <Row>
        <BackButton />
        {perspective?.clusterId && !isHistoryMode && (
          <Row alignItems="center" columnGap={1}>
            <IconCluster size={24} color={styles.clusterText.color} />
            <Text
              variant="titleSmall"
              style={styles.clusterText}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {perspective.cluster?.topic || ''}
            </Text>
          </Row>
        )}
      </Row>
      {isHistoryMode && perspective ? (
        <Row
          style={{ marginBottom: 2 }}
          flex={1}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <ContributionCount
            perspectiveId={perspective.id}
            contributions={perspective.contributions.filter(
              (c) =>
                c.status === ContributionStatus.Pending ||
                c.status === ContributionStatus.Accepted
            )}
            visible={false}
            handleClick={(value) => {
              navigation.navigate('PerspectiveContributions', {
                contributionTypeFilter: value,
                articleId: perspective.id,
              })
            }}
          />
        </Row>
      ) : null}

      <Row justifyContent={'flex-end'} alignItems={'center'}>
        <View style={{ marginBottom: 2 }}>
          <ArticleModeButton />
        </View>

        <PerspectiveScreenBarMenu
          perspective={perspective}
          navigation={navigation}
        />
      </Row>
    </Appbar.Header>
  )
}

const useStyle = createStyle(({ theme, isMobile }) => ({
  container: {
    backgroundColor: theme.palettes.primary[96]!,
    justifyContent: 'space-between',
    paddingHorizontal: isMobile ? 8 : 20,
  },
  clusterText: {
    color: theme.colors.onSurface,
    fontSize: 18,
    maxWidth: 200,
  },
}))
